import { CategorieIllustration } from "@/assets/images/svgIcons/illustrations/categorieIllustration"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { TemplateListItem } from "@/modules/template/components/sidebarImport/templateListItem"
import { TemplateType } from "@/modules/template/types/templateType"
import { useTemplatesInfinite } from "@/modules/template/hooks/useTemplate"
import { useCallback, useEffect, useMemo, useRef } from "react"
import { word } from "@/modules/core/utils/i18n"
import {
  useTemplateImportSearchTerm,
  useTemplateImportSelectedTemplate,
} from "@/modules/template/stores/useTemplateImportStore"

type TemplateListProps = {
  templateType: TemplateType
  className?: string
} & React.ComponentProps<"div">

export function TemplateList({ templateType, className, ...props }: TemplateListProps) {
  const selectedTemplate = useTemplateImportSelectedTemplate()
  const searchTerm = useTemplateImportSearchTerm()
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useTemplatesInfinite({
    templateTypeFilter: templateType,
    searchTerm,
  })

  const emptyTemplateType = useMemo(() => {
    switch (templateType) {
      case TemplateType.WEEK:
        return word("templateType.empty.week")
      case TemplateType.SESSION:
        return word("templateType.empty.session")
      case TemplateType.EXERCISE:
        return word("templateType.empty.exercise")
      default: {
        const exhaustiveCheck: never = templateType
        throw new Error(`Invalid template type ${String(exhaustiveCheck)}`)
      }
    }
  }, [templateType])

  const loadMoreRef = useRef<HTMLDivElement>(null)

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const isIntersecting = entries[0]?.isIntersecting
      if (isIntersecting && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  )

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: "100px",
      threshold: 0.1,
    })

    const currentRef = loadMoreRef.current
    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      observer.disconnect()
    }
  }, [handleIntersection])

  const filteredData = selectedTemplate ? data.filter((template) => template.id !== selectedTemplate.id) : data

  return (
    <div className={cn("flex flex-1 flex-col", className)} {...props}>
      {data.length === 0 ? (
        <TemplateListEmptyOrNotFound text={emptyTemplateType} />
      ) : data.length === 0 && searchTerm ? (
        <TemplateListEmptyOrNotFound text={word("global.noResultFor", { searchTerm })} />
      ) : (
        filteredData.map((template) => <TemplateListItem key={template.id} template={template} />)
      )}
    </div>
  )
}

type TemplateListEmptyOrNotFoundProps = {
  text: string
  className?: string
} & React.ComponentProps<"div">
function TemplateListEmptyOrNotFound({ text, className, ...props }: TemplateListEmptyOrNotFoundProps) {
  return (
    <div className={cn("flex w-full flex-col items-center justify-center gap-2 py-8", className)} {...props}>
      <CategorieIllustration color="currentColor" />
      <Text fontStyle="LL">{text}</Text>
    </div>
  )
}
